import { BuzzerStateContext } from "./CardsHomeScreen";
import { useContext } from "react";

export default function SessionStats({
    state,
    loadQuestions
}) {
    const buzzerStateContext = useContext(BuzzerStateContext);
    const hasMissed = state.missedQuestions.length > 0;
    const avgReaction = averageReactionMs(state.results);

    function averageReactionMs(results) {
        const values = results
            .map((r) => r.reactionMs)
            .filter((v) => v !== null);

        if (values.length === 0) return null;
        return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    }

    function buzzDecisionAccuracy(state) {
        const total =
            state.correctBuzzes +
            state.incorrectBuzzes +
            state.goodPasses +
            state.missedOpportunities;

        if (total === 0) return 0;

        const good = state.correctBuzzes + state.goodPasses;
        return Math.round((good / total) * 100);
    }

    function getFalseBuzzRate(state) {
        const totalBuzzes = state.correctBuzzes + state.incorrectBuzzes;
        if (totalBuzzes === 0) return 0;
        return Math.round((state.incorrectBuzzes / totalBuzzes) * 100);
    }

    function getSummaryMessage(state) {
        const accuracy = buzzDecisionAccuracy(state);
        const avgReaction = averageReactionMs(state.results);
        const falseBuzzRate = getFalseBuzzRate(state);
        const buzzCount = state.correctBuzzes + state.incorrectBuzzes;

        if (accuracy === 100 && buzzCount <= 1) {
            return "Perfect accuracy, but you were too cautious.";
        }

        if (accuracy >= 80 && avgReaction != null && avgReaction <= 1200) {
            return "Excellent session — strong decisions and solid timing.";
        }

        if (accuracy >= 80) {
            return "Strong decisions, but your timing can improve.";
        }

        if (falseBuzzRate >= 40) {
            return "Aggressive buzzing hurt your session.";
        }

        return "A mixed session with room to improve.";
    }

    function getInsights(state) {
        const accuracy = buzzDecisionAccuracy(state);
        const falseBuzzRate = getFalseBuzzRate(state);
        const avgReaction = averageReactionMs(state.results);
        const buzzCount = state.correctBuzzes + state.incorrectBuzzes;
        const buzzRate = state.questions.length > 0 ? buzzCount / state.questions.length : 0;

        const insights = [];

        if (buzzCount === 0) {
            return ["You didn’t buzz this round — try taking more chances."];
        }

        if (buzzRate < 0.2) {
            insights.push("You were very selective — consider buzzing more often.");
        } else if (falseBuzzRate > 0.4) {
            insights.push("You were too aggressive — be more selective before buzzing.");
        } else if (buzzRate > 0.6 && falseBuzzRate < 0.2) {
            insights.push("You buzzed confidently without making costly mistakes.");
        } else {
            insights.push("Your decision-making was solid overall.");
        }

        if (avgReaction != null && avgReaction > 1400) {
            insights.push("Your reaction time was slower than ideal.");
        } else if (avgReaction != null && avgReaction < 1000) {
            insights.push("Your reaction time was strong.");
        }

        if (accuracy < 1 && state.goodPasses === 0 && buzzRate > 0.3) {
            insights.push("Consider using passes more strategically.");
        }

        return insights.slice(0, 3);
    }

    function onReview() {
        buzzerStateContext.dispatch({
            type: "START_REVIEW"
        });
    }

    return (
        <div className="end-screen">
            <div className="end-screen__content">
                <div className="end-screen__header">
                    <div className="end-screen__title"></div>
                    <div className="end-screen__subtitle">
                    </div>
                </div>

                <div className="session-summary-card">
                    <div className="session-summary-card__eyebrow">
                        Final Score
                    </div>
                    <div className="session-summary-card__score">
                        {state.score}
                    </div>
                    <div className="session-summary-card__message">
                        {getSummaryMessage(state)}
                    </div>
                </div>

                <div className="session-stats">
                    <div className="session-stats__grid">
                        <StatCard label="Accuracy" value={`${buzzDecisionAccuracy(state)}%`} />
                        <StatCard label="Avg reaction" value={avgReaction != null ? `${avgReaction} ms` : "—"} />
                        <StatCard label="False buzz rate" value={`${getFalseBuzzRate(state)}%`} />
                        <StatCard label="Passes" value={state.goodPasses.toString()} />
                        <StatCard label="Current Streak" value={localStorage.getItem("currentStreak")} />
                        <StatCard label="Best Streak" value={localStorage.getItem("bestStreak")} />
                    </div>
                </div>

                <div className="session-insights">
                    <div className="session-insights__title">Takeaways</div>
                    <ul className="session-insights__list">
                        {getInsights(state).map((text, i) => (
                            <li key={i}>{text}</li>
                        ))}
                    </ul>
                </div>

                <div className="session-actions">
                    <button className="session-actions__primary" onClick={() => loadQuestions(state.mode)}>
                        Play Again
                    </button>
                </div>

                <div className="session-actions__secondary">
                    {hasMissed && (
                        <button
                            className="session-actions__review"
                            onClick={onReview}>Review Missed</button>
                    )}
                </div>
            </div>
        </div>
    );
}

function StatCard({ label, value }) {
    return (
        <div className="session-stats__item">
            <div className="session-stats__label">{label}</div>
            <div className="session-stats__value">{value}</div>
        </div>
    );
}